import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { downloadFile } from '../../../functions/download-file';
import { CompletedReport } from '../../../models/api/reports/all-reports-response';
import { Action } from '../../../models/core/action';
import { CasePipe } from '../../../pipes/case.pipe';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { TagComponent } from '../../core/labels/tag.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import {
  TableComponent,
  TableRemoveRowEvent,
} from '../../core/table/table.component';
import { FormatPipe } from '../../../pipes/format.pipe';

@Component({
  selector: 'app-completed-reports',
  imports: [
    TableComponent,
    ColumnComponent,
    TagComponent,
    MenuComponent,
    CommonModule,
    IconButtonComponent,
    LinkLabelComponent,
    ActionButtonComponent,
    CasePipe,
    FormatPipe,
  ],
  template: `
    <div>
      <div class="tw-pt-2">
        <app-table
          [dataSource]="completedReports()"
          [showSelection]="true"
          selectionKey="completedRequestOutputId"
          [showRemove]="true"
          [showRemoveAll]="true"
          recordLabel="completed report"
          [selectionLabel]="getCompletedReportSelectionLabel"
          dataKey="completedRequestOutputId"
          scrollHeight="2000px"
          [stripedRows]="false"
          [showRefresh]="true"
          [showFilterButton]="false"
          [showFilter]="false"
          [loading]="loading()"
          (refresh)="refresh()"
          sortField="dateCompleted"
          [sortOrder]="-1"
          emptyIcon="fa-light fa-file"
          emptyMessage="Click the New report button to create and run a new report or edit a schedule from the Scheduled tab."
          (removeRows)="onRemoveRows($event)"
          caption="Completed reports"
          #completedTable
        >
          <app-column
            name="reportName"
            label="Name"
            filterType="select"
            [wrap]="true"
            minWidth="320px"
            maxWidth="420px"
          >
            <ng-template let-data>
              <div
                class="tw-flex tw-items-center tw-gap-2 tw-max-w-full tw-break-words tw-whitespace-normal"
              >
                <app-link-label
                  class="tw-min-w-0"
                  [href]="getReportDetailHref(data)"
                  (click)="viewReportDetails(data)"
                  style="cursor: pointer;"
                >
                  {{ data.reportName }}
                </app-link-label>
                <div class="tw-shrink-0">
                  <app-tag
                    [type]="data.fileFormat === 'ERROR' ? 'Error' : 'Purple'"
                  >
                    {{ data.fileFormat }}
                  </app-tag>
                </div>
                @if (data.fileFormat !== 'ERROR') {
                  <app-icon-button
                    #downloadButton
                    class="tw-hidden md:tw-inline-flex"
                    icon="fa-regular fa-arrow-down-to-line"
                    tooltip="Click to download report"
                    (click)="downloadReport(data, downloadButton)"
                  />
                }
              </div>
            </ng-template>
          </app-column>

          <app-column name="outputName" label="Base report" filterType="select"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.outputName }}
              </div>
            </ng-template>
          </app-column>
          <app-column name="filterName" label="Filter" filterType="select"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.filterName }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="contract"
            label="Client"
            filterType="select"
            filterLabelName="clientName"
            filterLabelCase="titlecase"
          >
            <ng-template let-data>
              <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                @if (data.contract && data.clientName) {
                  {{ data.contract }} -
                  {{ data.clientName | case: 'titlecase' }}
                }
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="dateCompleted"
            label="Completed"
            format="dt"
            filterType="date"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.dateCompleted | format: 'dt' }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="actions"
            label="Actions"
            classes=""
            [frozen]="true"
            [sort]="false"
          >
            <ng-template let-data>
              <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                <app-action-button
                  [ariaLabel]="'More actions for ' + data.reportName"
                  [menu]="rowMenu"
                  [row]="data"
                  (click)="
                    setCurrentMenuData(data); rowMenu.toggle($event, data)
                  "
                />
              </div>
            </ng-template>
          </app-column>
        </app-table>
      </div>
    </div>
    <app-menu
      [items]="currentRowActions()"
      #rowMenu
      (click)="onAction($event, rowMenu.data)"
    />
  `,
  styles: ``,
})
export class CompletedReportsComponent extends BaseComponent implements OnInit {
  @ViewChild('completedTable')
  completedTable: TableComponent;

  private readonly snackbar = inject(Snackbar);
  private readonly reportsService = inject(ReportsService);
  protected override router = inject(Router);

  rowActions: Action[] = [
    { label: 'View details', key: 'view' },
    { label: 'Download', key: 'download' },
    { label: 'Delete', key: 'delete' },
  ];

  readonly completedReports = this.reportsService.completedReports;
  readonly loading = this.reportsService.completedLoading;

  currentRowActions = signal<Action[]>([]);

  setCurrentMenuData(data: CompletedReport): void {
    if (data.fileFormat === 'ERROR') {
      this.currentRowActions.set(
        this.rowActions.filter((action) => action.key !== 'download'),
      );
    } else {
      this.currentRowActions.set([...this.rowActions]);
    }
  }

  /**
   * Returns the report name for the selection checkbox aria-label.
   * Used for accessibility to announce which report is being selected.
   */
  getCompletedReportSelectionLabel = (row: CompletedReport): string => {
    return row?.reportName?.trim() || 'completed report';
  };

  constructor() {
    super();
  }

  async ngOnInit() {
    await this.reportsService.getReports();
  }

  async onRemoveRows(event: TableRemoveRowEvent) {
    if (event.type === 'selected') {
      this.deleteSelectedReports();
    } else if (event.type === 'all') {
      this.deleteAllReports();
    }
  }
  onAction(action: Action, report: CompletedReport) {
    if (action.key === 'view') {
      this.viewReportDetails(report);
    } else if (action.key === 'download') {
      this.downloadReport(report, null);
    } else if (action.key === 'delete') {
      const dateCompleted = new Date(report.dateCompleted)
        .toLocaleString()
        .replace(',', '');

      this.confirm.show({
        label: 'Delete completed report',
        message: `Are you sure you want to delete ${
          report.reportName + ` (${dateCompleted})`
        }?`,
        width: '35rem',
        data: report,
        confirm: (data) => this.deleteReport(data),
      });
    }
  }

  getReportDetailHref(report: CompletedReport): string {
    return this.reportsService.getDetailUrl(report, 'completed');
  }

  viewReportDetails(report: CompletedReport) {
    this.reportsService.navigateToDetail(report, 'completed');
  }

  async deleteReport(report: CompletedReport) {
    await this.reportsService.deleteCompletedReport(
      report.completedRequestId,
      report.completedRequestOutputId,
    );
  }

  deleteSelectedReports() {
    const tableSelection = this.completedTable?.getSelection() || [];

    if (tableSelection.length === 0) {
      this.snackbar.show('No items selected', 'Info');
      return;
    }

    const count = tableSelection.length;
    const reportNames = tableSelection
      .map((r) => r.reportName)
      .join('<br>\u2022');
    const dateCompleted = new Date(tableSelection[0].dateCompleted)
      .toLocaleString()
      .replace(',', '');
    const fullCount = this.completedReports().length;
    let message;

    if (count === 1) {
      message = `Are you sure you want to delete ${
        reportNames + ` (${dateCompleted})?`
      }`;
    } else if (count === fullCount) {
      message = `Are you sure you want to delete all ${count} completed reports?`;
    } else {
      message = `Are you sure you want to delete ${count} completed reports?`;
    }

    this.confirm.show({
      label: 'Delete completed report',
      message: message,
      width: '45rem',
      data: tableSelection,
      confirm: (data) => this.executeDeleteMultiple(data),
    });
  }

  async executeDeleteMultiple(reportsToDelete: CompletedReport[]) {
    const deleteItems = reportsToDelete.map((report) => ({
      completedRequestId: report.completedRequestId,
      completedOutputRequestId: report.completedRequestOutputId,
    }));

    await this.reportsService.deleteMultipleCompletedReports(deleteItems);
  }

  deleteAllReports() {
    if (this.completedReports().length === 0) {
      this.snackbar.show('No reports to delete', 'Info');
      return;
    }

    const message = 'Are you sure you want to delete all completed reports?';

    this.confirm.show({
      label: 'Delete completed report',
      message: message,
      width: '45rem',
      data: this.completedReports(),
      confirm: (data) => this.executeDeleteMultiple(data),
    });
  }

  async downloadReport(
    report: CompletedReport,
    downloadButton: IconButtonComponent,
  ) {
    if (downloadButton) {
      downloadButton.disabled = true;
    }

    try {
      const blob = await this.reportsService.downloadCompletedReport(
        'JURIS',
        report.completedRequestOutputId,
      );

      const filename =
        report.filename || `report-${report.completedRequestOutputId}.pdf`;

      downloadFile(blob, filename, blob.type);
    } finally {
      if (downloadButton) {
        downloadButton.disabled = false;
      }
    }
  }

  async refresh() {
    await this.reportsService.getReports(true);
  }
}
